import { useEffect, useState } from 'react';

import { Badge, Box, Flex, IconButton, Link, Text, Wrap, WrapItem } from '@chakra-ui/react';
import { RiRefreshLine } from 'react-icons/ri';

import { useGetCallHistoryQuery } from '../../gql/generated/graphql';

import LETable from '../../components/shared/Table/LETable';
import { TableColumns } from '../../components/shared/Table/types';

import { CallHistoryFilterValues } from './CallHistoryFilters';
import { formatDuration } from './utils';

import usePaginationControls from '../../hooks/usePaginationControls';
import { capitalizeFirstLetter } from '../../utils/text';

type CallHistoryRow = {
  id: string;
  conference_id?: string | null;
  direction?: string | null;
  flow?: string | null;
  outcome?: string | null;
  fromNumber?: string | null;
  calledNumber?: string | null;
  agent_name?: string | null;
  created_at: string;
  callDuration?: string | null;
  sip_failure_count?: number | null;
  was_transferred?: boolean | null;
  hunt_group_slug?: string | null;
  customer_name?: string | null;
  hunt_group_name?: string | null;
  deal_id?: number | null;
  recording_url?: string | null;
  participants?: string | null;
};

// Color maps

// Explicit bg/color pairs instead of colorScheme + variant="subtle" because
// green.100 in Chakra's default theme (#c6f6d5) is nearly indistinguishable
// from white. Using .200 shades ensures every outcome has clearly visible color.
const OUTCOME_BADGE_STYLES: Record<string, { bg: string; color: string }> = {
  answered: { bg: 'green.200', color: 'green.800' },
  voicemail: { bg: 'orange.200', color: 'orange.800' },
  noanswer: { bg: 'red.200', color: 'red.800' },
  hungup: { bg: 'purple.200', color: 'purple.800' },
};

const FLOW_COLORS: Record<string, string> = {
  sip: 'blue',
  huntgroup: 'orange',
  direct: 'teal',
  outbound: 'cyan',
};

// Only vivid colorSchemes that render clearly with variant="subtle".
// Removed 'green' and 'cyan' which appear nearly white in Chakra's subtle palette.
const PARTICIPANT_COLORS = ['pink', 'purple', 'orange', 'red', 'blue', 'teal', 'yellow'];

// Deterministic color per participant name — same person always gets the same
// badge color across all rows.
const getParticipantColor = (name: string): string => {
  const hash = name.split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  return PARTICIPANT_COLORS[hash % PARTICIPANT_COLORS.length];
};

const formatFlow = (flow: string): string => {
  const lower = flow.toLowerCase();
  if (lower === 'huntgroup') {
    return 'Huntgroup';
  }
  return capitalizeFirstLetter(lower);
};

const formatDateTime = (ts: string): string => {
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return ts;
  }
};

// Null placeholder

const Dash = () => (
  <Text fontSize="sm" color="gray.400">
    —
  </Text>
);

// Columns

const COLUMNS: TableColumns<CallHistoryRow> = [
  {
    header: 'Date/Time',
    getComponent: ({ item }) => (
      <Text fontSize="sm" whiteSpace="nowrap">
        {formatDateTime(item.created_at)}
      </Text>
    ),
  },
  {
    header: 'Caller',
    getComponent: ({ item }) => (
      <Box>
        {item.customer_name != null && (
          <Text fontSize="sm" fontWeight="semibold" lineHeight="short">
            {item.customer_name}
          </Text>
        )}
        <Text fontSize="xs" color="gray.500">
          {item.fromNumber ?? '—'}
        </Text>
      </Box>
    ),
  },
  {
    header: 'Called #',
    getComponent: ({ item }) => {
      const calledPartyName = item.hunt_group_name ?? item.agent_name;
      return (
        <Box>
          {calledPartyName != null && (
            <Text fontSize="sm" fontWeight="medium" lineHeight="short">
              {calledPartyName}
            </Text>
          )}
          <Text fontSize="xs" color="gray.500">
            {item.calledNumber ?? '—'}
          </Text>
        </Box>
      );
    },
  },
  {
    header: 'Flow',
    getComponent: ({ item }) =>
      item.flow != null ? (
        <Badge
          colorScheme={FLOW_COLORS[item.flow.toLowerCase()] ?? 'gray'}
          variant="subtle"
          borderRadius="full"
          px={2.5}
          py={0.5}
          fontSize="xs"
          fontWeight="medium"
          textTransform="none"
        >
          {formatFlow(item.flow)}
        </Badge>
      ) : (
        <Dash />
      ),
  },
  {
    header: 'Agent',
    getComponent: ({ item }) => <Text fontSize="sm">{item.agent_name ?? '—'}</Text>,
  },
  {
    header: 'Outcome',
    getComponent: ({ item }) => {
      // Normalize to lowercase so DB values like 'Answered' or 'answered' both match.
      const outcomeKey = (item.outcome ?? '').toLowerCase();
      const style = OUTCOME_BADGE_STYLES[outcomeKey];
      return outcomeKey ? (
        <Badge
          bg={style?.bg ?? 'gray.200'}
          color={style?.color ?? 'gray.700'}
          borderRadius="full"
          px={2.5}
          py={0.5}
          fontSize="xs"
          fontWeight="medium"
          textTransform="none"
        >
          {capitalizeFirstLetter(outcomeKey)}
        </Badge>
      ) : (
        <Dash />
      );
    },
  },
  {
    header: 'Duration',
    getComponent: ({ item }) => (
      <Text fontSize="sm" sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {formatDuration(item.callDuration)}
      </Text>
    ),
  },
  {
    header: 'SIP Failures',
    getComponent: ({ item }) =>
      (item.sip_failure_count ?? 0) > 0 ? (
        <Badge colorScheme="red" variant="subtle" borderRadius="full" px={2} fontSize="xs">
          {item.sip_failure_count}
        </Badge>
      ) : (
        <Text fontSize="sm" color="gray.400">
          0
        </Text>
      ),
  },
  {
    header: 'Transferred',
    getComponent: ({ item }) =>
      item.was_transferred ? (
        <Badge colorScheme="blue" variant="subtle" borderRadius="full" px={2} fontSize="xs">
          Yes
        </Badge>
      ) : (
        <Text fontSize="sm" color="gray.400">
          No
        </Text>
      ),
  },
  {
    header: 'Deal',
    getComponent: ({ item }) =>
      item.deal_id != null ? (
        <Link
          href={`${window.location.origin}/deals/${item.deal_id}`}
          color="blue.500"
          fontSize="sm"
          fontWeight="medium"
          isExternal
          _hover={{ color: 'blue.700', textDecoration: 'underline' }}
        >
          {item.deal_id}
        </Link>
      ) : (
        <Dash />
      ),
  },
  {
    header: 'Recording',
    getComponent: ({ item }) =>
      item.recording_url != null ? (
        <Link
          href={item.recording_url}
          color="blue.500"
          fontSize="sm"
          isExternal
          _hover={{ color: 'blue.700', textDecoration: 'underline' }}
        >
          Link to Recording
        </Link>
      ) : (
        <Dash />
      ),
  },
  {
    header: 'Participants',
    getComponent: ({ item }) => {
      if (item.participants == null) {
        return <Dash />;
      }
      const names = item.participants.split(', ').filter(Boolean);
      if (names.length === 0) {
        return <Dash />;
      }
      return (
        <Wrap spacing={1}>
          {names.map((name) => (
            <WrapItem key={name}>
              <Badge
                colorScheme={getParticipantColor(name)}
                variant="subtle"
                borderRadius="full"
                px={2.5}
                py={0.5}
                fontSize="xs"
                fontWeight="medium"
                textTransform="none"
              >
                {name}
              </Badge>
            </WrapItem>
          ))}
        </Wrap>
      );
    },
  },
  {
    header: 'Call SID',
    getComponent: ({ item }) => (
      <Text fontSize="xs" fontFamily="mono" color="gray.600" whiteSpace="nowrap">
        {item.id}
      </Text>
    ),
  },
  {
    header: 'Conference ID',
    getComponent: ({ item }) =>
      item.conference_id ? (
        <Text fontSize="xs" fontFamily="mono" color="gray.600" whiteSpace="nowrap">
          {item.conference_id}
        </Text>
      ) : (
        <Dash />
      ),
  },
];

// Component

type CallHistoryTableProps = {
  filters: CallHistoryFilterValues;
  onSelectCall: (conferenceId: string | null, callId: string) => void;
};

const CallHistoryTable = ({ filters, onSelectCall }: CallHistoryTableProps) => {
  const paginationControls = usePaginationControls({
    initialItemsPerPage: 25,
    initialItemsPerPageOptions: [25, 50, 100],
  });
  const { currentPage, itemsPerPage, setTotalRecords, setCurrentPage } = paginationControls;
  const [isRefreshing, setIsRefreshing] = useState(false);

  const { data, loading, refetch } = useGetCallHistoryQuery({
    variables: {
      page: currentPage,
      itemsPerPage,
      dateFrom: filters.dateFrom || undefined,
      dateTo: filters.dateTo || undefined,
      agentUserId: filters.agentUserId || undefined,
      outcome: filters.outcome || undefined,
      flowType: filters.flowType || undefined,
      hasEventType: filters.hasEventType || undefined,
      callSid: filters.callSid || undefined,
      conferenceId: filters.conferenceId || undefined,
    },
  });

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refetch();
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    if (data?.getCallHistory?.totalRecords != null) {
      setTotalRecords(data.getCallHistory.totalRecords);
    }
  }, [data?.getCallHistory?.totalRecords, setTotalRecords]);

  useEffect(() => {
    setCurrentPage(0);
  }, [filters, setCurrentPage]);

  const calls: CallHistoryRow[] = data?.getCallHistory?.calls ?? [];

  const handleSelectRow = (item?: CallHistoryRow) => {
    if (item != null) {
      onSelectCall(item.conference_id ?? null, item.id);
    }
  };

  return (
    <Box>
      <Flex justifyContent="flex-end" mb={2}>
        <IconButton
          aria-label="Refresh call events"
          icon={<RiRefreshLine />}
          size="sm"
          variant="ghost"
          colorScheme="gray"
          isLoading={isRefreshing}
          onClick={handleRefresh}
        />
      </Flex>
      <LETable<CallHistoryRow>
        columns={COLUMNS}
        rowKeyPrefix="call-history"
        rowKeyProp="id"
        items={calls}
        isLoading={loading}
        paginationControls={paginationControls}
        customOnClick={handleSelectRow}
      />
    </Box>
  );
};

export default CallHistoryTable;
